import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
    Home, FileText, CreditCard, LineChart, UserCircle2,
    ChevronLeft, Loader2, Mail, X, Plus, Users, Calendar, Map,
    Crown, LogOut, Trash2, AlertTriangle, UserPlus, Pencil, Check, RefreshCw,
    Lock, ArchiveX, ClipboardList, ArrowLeftRight,
} from "lucide-react";
import axios from "axios";
import { getGroupTypeConfig } from "../utils/formatters";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PRIMARY = "#2D5BFF";
const PRIMARY_DARK = "#1B3DBF";
const GRADIENT = `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`;

const NAV_ITEMS = [
    { icon: Home, label: "Home", key: "home" },
    { icon: FileText, label: "Receipts", key: "receipts" },
    { icon: CreditCard, label: "Cards", key: "cards" },
    { icon: LineChart, label: "Insights", key: "insights" },
    { icon: UserCircle2, label: "Profile", key: "profile" },
];

const GROUP_EMOJIS = [
    "🏠","🏡","🏢","👨‍👩‍👧‍👦","✈️","🚗","🏖️","⛺",
    "🎉","🎂","💍","🎓","⚽","🏋️","🎮","🎵",
    "🛒","🍕","☕","💼","📚","🌍","❤️","⭐",
];

export default function GroupDetailPage() {
    const { user: authUser } = useAuth();
    const { groupId } = useParams();
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(false);
    const [loading, setLoading] = useState(true);
    const [group, setGroup] = useState(null);
    const [members, setMembers] = useState([]);
    const [sharedCounts, setSharedCounts] = useState({ receipts: 0, cards: 0, vouchers: 0 });
    const [myRole, setMyRole] = useState(null);

    // Add member state (invite + guest unified)
    const [showInvite, setShowInvite] = useState(false);
    const [inviteName, setInviteName] = useState("");
    const [inviteEmail, setInviteEmail] = useState("");
    const [addingMember, setAddingMember] = useState(false);
    // Add email to placeholder
    const [addEmailFor, setAddEmailFor] = useState(null); // user_id of placeholder
    const [addEmailValue, setAddEmailValue] = useState("");
    const [addingEmail, setAddingEmail] = useState(false);


    // Edit group name/emoji state
    const [editingGroup, setEditingGroup] = useState(false);
    const [editName, setEditName] = useState("");
    const [editEmoji, setEditEmoji] = useState(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [saving, setSaving] = useState(false);

    // Leave / delete state
    const [confirmLeave, setConfirmLeave] = useState(false);
    const [leaving, setLeaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // Close / reopen / permanent delete state
    const [confirmClose, setConfirmClose] = useState(false);
    const [closing, setClosing] = useState(false);
    const [confirmReopen, setConfirmReopen] = useState(false);
    const [reopening, setReopening] = useState(false);
    const [confirmPermanentDelete, setConfirmPermanentDelete] = useState(false);
    const [permanentDeleting, setPermanentDeleting] = useState(false);

    // Reimbursement tracking
    const [showEnableReimb, setShowEnableReimb] = useState(false);
    const [enablingReimb, setEnablingReimb] = useState(false);
    const [reimbDefaultSource, setReimbDefaultSource] = useState('none');
    const [reimbDefaultMemberId, setReimbDefaultMemberId] = useState(null);
    const [reimbDefaultMemberName, setReimbDefaultMemberName] = useState('');
    const [reimbDefaultLabelName, setReimbDefaultLabelName] = useState('');
    // Payment sources (labels) management
    const [circleLabels, setCircleLabels] = useState([]);
    const [editingLabelId, setEditingLabelId] = useState(null);
    const [editingLabelName, setEditingLabelName] = useState('');
    const [savingLabelRename, setSavingLabelRename] = useState(false);
    const [showAddPaymentLabel, setShowAddPaymentLabel] = useState(false);
    const [newPaymentLabelName, setNewPaymentLabelName] = useState('');
    const [newPaymentLabelType, setNewPaymentLabelType] = useState('bank');
    const [addingPaymentLabel, setAddingPaymentLabel] = useState(false);

    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 768);
        check();
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
    }, []);

    const fetchGroup = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/groups/${groupId}`, { withCredentials: true });
            if (res.data.success) {
                setGroup(res.data.group);
                setMembers(res.data.members || []);
                setSharedCounts(res.data.shared_counts || { receipts: 0, cards: 0, vouchers: 0 });
                setMyRole(res.data.my_role);
            }
        } catch (err) {
            if (err.response?.status === 404) {
                navigate("/profile", { replace: true });
            }
        } finally {
            setLoading(false);
        }
    }, [groupId, navigate]);

    useEffect(() => { fetchGroup(); }, [fetchGroup]);

    const onNavClick = (key) => {
        if (key === "home") navigate("/dashboard");
        else if (key === "receipts") navigate("/receipts");
        else if (key === "cards") navigate("/cards");
        else if (key === "insights") navigate("/insights");
        else if (key === "profile") navigate("/profile");
    };

    // ── Add member (invite with email OR guest without email) ───────────────
    const handleAddMember = async () => {
        const name = inviteName.trim();
        const email = inviteEmail.trim().toLowerCase() || undefined;
        if (!name) { toast.error("Name is required"); return; }
        setAddingMember(true);
        try {
            const res = await axios.post(`${API}/groups/${groupId}/add-member`, {
                name, email, role: "contributor",
            }, { withCredentials: true });
            if (res.data.success) {
                toast.success(res.data.type === "invite" ? "Invite sent!" : `${name} added as guest`);
                setInviteName(""); setInviteEmail(""); setShowInvite(false);
                fetchGroup();
            }
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Couldn't add member");
        } finally { setAddingMember(false); }
    };

    // ── Add email to guest placeholder ────────────────────────────────────────
    const handleAddEmail = async (userId) => {
        const email = addEmailValue.trim().toLowerCase();
        if (!email || !email.includes("@")) { toast.error("Enter a valid email"); return; }
        setAddingEmail(true);
        try {
            await axios.put(`${API}/groups/${groupId}/members/${userId}/add-email`, { email }, { withCredentials: true });
            toast.success("Email added — they'll get a notification to sign in");
            setAddEmailFor(null); setAddEmailValue(""); fetchGroup();
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Couldn't add email");
        } finally { setAddingEmail(false); }
    };

    // ── Cancel invite handler ────────────────────────────────────────────────
    const handleCancelInvite = async (memberId) => {
        try {
            await axios.post(`${API}/groups/${groupId}/cancel-invite/${memberId}`, {}, { withCredentials: true });
            fetchGroup();
        } catch { /* silent */ }
    };

    // ── Resend invite handler (24h rate limited) ─────────────────────────────
    const [resending, setResending] = useState(null);
    const [resendBlocked, setResendBlocked] = useState(null); // member_id that hit 24h limit

    const handleResendInvite = async (memberId, email) => {
        setResending(memberId);
        setResendBlocked(null);
        try {
            const res = await axios.post(
                `${API}/groups/${groupId}/resend-invite/${memberId}`,
                {},
                { withCredentials: true }
            );
            if (res.data.success) fetchGroup();
        } catch (err) {
            if (err.response?.status === 429 || err.response?.data?.detail === "already_sent_today") {
                setResendBlocked(memberId); // Show inline message instead of toast
            }
        } finally {
            setResending(null);
        }
    };

    // ── Leave group ──────────────────────────────────────────────────────────
    const handleLeave = async () => {
        setLeaving(true);
        try {
            const res = await axios.post(`${API}/groups/${groupId}/leave`, {}, { withCredentials: true });
            if (res.data.success) navigate("/profile", { replace: true });
        } catch { /* silent */ } finally {
            setLeaving(false);
            setConfirmLeave(false);
        }
    };

    // ── Close circle ─────────────────────────────────────────────────────────
    const handleDelete = async () => {
        setDeleting(true);
        try {
            const res = await axios.delete(`${API}/groups/${groupId}`, { withCredentials: true });
            if (res.data.success) navigate("/profile", { replace: true });
        } catch { /* silent */ } finally {
            setDeleting(false);
            setConfirmDelete(false);
        }
    };

    const handleClose = async () => {
        setClosing(true);
        try {
            const res = await axios.post(`${API}/groups/${groupId}/close`, {}, { withCredentials: true });
            if (res.data.success) fetchGroup();
        } catch { /* silent */ } finally {
            setClosing(false);
            setConfirmClose(false);
        }
    };

    const handleReopen = async () => {
        setReopening(true);
        try {
            const res = await axios.post(`${API}/groups/${groupId}/reopen`, {}, { withCredentials: true });
            if (res.data.success) fetchGroup();
        } catch { /* silent */ } finally {
            setReopening(false);
            setConfirmReopen(false);
        }
    };

    const handlePermanentDelete = async () => {
        setPermanentDeleting(true);
        try {
            const res = await axios.delete(`${API}/groups/${groupId}/permanent`, { withCredentials: true });
            if (res.data.success) navigate("/profile", { replace: true });
        } catch { /* silent */ } finally {
            setPermanentDeleting(false);
            setConfirmPermanentDelete(false);
        }
    };

    const handleEnableReimbursement = async () => {
        setEnablingReimb(true);
        try {
            const res = await axios.post(`${API}/groups/${groupId}/enable-reimbursement`, {}, { withCredentials: true });
            if (res.data.success) {
                setGroup(prev => prev ? { ...prev, reimbursement_tracking_enabled: true } : prev);
                setShowEnableReimb(false);
                toast.success(`Reimbursement tracking enabled${res.data.migrated_count ? ` · ${res.data.migrated_count} existing receipt${res.data.migrated_count !== 1 ? 's' : ''} updated` : ''}`);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't enable");
        } finally { setEnablingReimb(false); }
    };

    // ── Remove member ────────────────────────────────────────────────────────
    const handleRemoveMember = async (memberId, memberName) => {
        if (!window.confirm(`Remove ${memberName} from this circle?`)) return;
        try {
            await axios.delete(`${API}/groups/${groupId}/members/${memberId}`, { withCredentials: true });
            fetchGroup();
        } catch { /* silent */ }
    };

    // ── Payment labels ───────────────────────────────────────────────────────
    const fetchLabels = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/groups/${groupId}/labels`, { withCredentials: true });
            setCircleLabels(res.data.labels || []);
        } catch { /* silent */ }
    }, [groupId]);

    useEffect(() => {
        if (group?.reimbursement_tracking_enabled) fetchLabels();
    }, [group?.reimbursement_tracking_enabled, fetchLabels]);

    const handleRenameLabel = async (labelId) => {
        if (!editingLabelName.trim()) return;
        setSavingLabelRename(true);
        try {
            await axios.put(`${API}/groups/${groupId}/labels/${labelId}`, { name: editingLabelName.trim() }, { withCredentials: true });
            setCircleLabels(prev => prev.map(l => l.id === labelId ? { ...l, name: editingLabelName.trim() } : l));
            setEditingLabelId(null);
            toast.success('Label renamed');
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't rename");
        } finally { setSavingLabelRename(false); }
    };

    const handleAddPaymentLabel = async () => {
        if (!newPaymentLabelName.trim()) return;
        setAddingPaymentLabel(true);
        try {
            const res = await axios.post(`${API}/groups/${groupId}/labels`, { name: newPaymentLabelName.trim(), label_type: newPaymentLabelType }, { withCredentials: true });
            setCircleLabels(prev => [...prev, res.data.label]);
            setNewPaymentLabelName('');
            setShowAddPaymentLabel(false);
            toast.success('Payment source added');
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't add label");
        } finally { setAddingPaymentLabel(false); }
    };

    const handleUpdateDefault = async (userIdOrNull, nameOrNull, labelNameOrNull) => {
        try {
            const body = {
                paid_by_type: userIdOrNull ? 'member' : labelNameOrNull ? 'label' : 'none',
                paid_by_user_id: userIdOrNull || null,
                paid_by_name: nameOrNull || null,
                paid_by_label_name: labelNameOrNull || null,
            };
            await axios.put(`${API}/groups/${groupId}/default-reimb`, body, { withCredentials: true });
            setGroup(prev => prev ? { ...prev, default_reimb_user_id: userIdOrNull, default_reimb_name: nameOrNull, default_reimb_label_name: labelNameOrNull } : prev);
            toast.success('Default updated');
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : "Couldn't update default");
        }
    };

    // ── Save group name/emoji ────────────────────────────────────────────────
    const startEditing = () => {
        setEditName(group.name);
        setEditEmoji(group.emoji || null);
        setEditingGroup(true);
        setShowEmojiPicker(false);
    };

    // Circle name: letters, numbers, spaces only — max 15 chars
    const sanitiseGroupName = (val) =>
        val.replace(/[^a-zA-Z0-9 ]/g, "").slice(0, 15);

    const handleSaveGroup = async () => {
        const trimmed = editName.trim();
        if (!trimmed) return; // empty — stay in edit mode, no toast
        setSaving(true);
        try {
            const res = await axios.put(`${API}/groups/${groupId}`, {
                name: trimmed,
                emoji: editEmoji,
            }, { withCredentials: true });
            if (res.data.success) {
                setGroup(res.data.group);
                setEditingGroup(false);
                setShowEmojiPicker(false);
            }
        } catch { /* silent */ } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#F9FAFB" }}>
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: PRIMARY }} />
            </div>
        );
    }

    if (!group) return null;

    const cfg = getGroupTypeConfig(group.type);
    const TypeIcon = cfg.icon;
    const activeMembers = members.filter(m => m.status === "active");
    const pendingMembers = members.filter(m => m.status === "pending");
    const isAdmin = myRole === "admin";

    // Count enabled sharing categories
    const sharing = group.sharing || {};
    const enabledCategories = [sharing.receipts, sharing.loyalty_cards, sharing.vouchers].filter(Boolean).length;

    // ── Header section (blue) ────────────────────────────────────────────────
    const headerContent = () => (
        <>
            {/* Group info */}
            <div className="flex items-center gap-4" style={{ marginTop: 20, marginBottom: editingGroup ? 12 : 20 }}>
                {/* Icon — tappable when editing */}
                <button
                    onClick={() => { if (editingGroup) setShowEmojiPicker(p => !p); }}
                    className="flex items-center justify-center flex-shrink-0"
                    style={{
                        width: 56, height: 56, borderRadius: 16,
                        backgroundColor: cfg.bg,
                        fontSize: 26, cursor: editingGroup ? "pointer" : "default",
                        border: editingGroup ? "2px solid rgba(255,255,255,0.5)" : "none",
                        position: "relative",
                    }}
                >
                    {editingGroup
                        ? (editEmoji
                            ? <span>{editEmoji}</span>
                            : <TypeIcon className="w-7 h-7" style={{ color: "#FFFFFF" }} />)
                        : (group.emoji
                            ? <span>{group.emoji}</span>
                            : <TypeIcon className="w-7 h-7" style={{ color: "#FFFFFF" }} />)
                    }
                    {editingGroup && (
                        <div className="absolute -bottom-1 -right-1 flex items-center justify-center"
                            style={{ width: 18, height: 18, borderRadius: 999, backgroundColor: "#FFFFFF" }}>
                            <Pencil className="w-2.5 h-2.5" style={{ color: cfg.bg }} />
                        </div>
                    )}
                </button>

                <div className="flex-1 min-w-0">
                    {editingGroup ? (
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <input
                                    autoFocus
                                    value={editName}
                                    onChange={(e) => setEditName(sanitiseGroupName(e.target.value))}
                                    onKeyDown={(e) => { if (e.key === "Enter") handleSaveGroup(); if (e.key === "Escape") setEditingGroup(false); }}
                                    style={{
                                        backgroundColor: "rgba(255,255,255,0.2)",
                                        border: "1.5px solid rgba(255,255,255,0.6)",
                                        borderRadius: 10, color: "#FFFFFF",
                                        caretColor: "#FFFFFF",
                                        fontSize: 20, fontWeight: 700,
                                        fontFamily: "Manrope, sans-serif",
                                        padding: "6px 10px", flex: 1,
                                        outline: "none",
                                    }}
                                    placeholder="Circle name"
                                    maxLength={15}
                                />
                                <span style={{
                                    color: editName.length >= 15 ? "#FCA5A5" : "rgba(255,255,255,0.6)",
                                    fontSize: 12, fontFamily: "DM Sans, sans-serif",
                                    flexShrink: 0, minWidth: 32,
                                }}>
                                    {editName.length}/15
                                </span>
                            </div>
                            <p style={{ color: "rgba(255,255,255,0.75)", fontSize: 14, fontFamily: "DM Sans, sans-serif", marginTop: 2 }}>
                                {cfg.label} · {activeMembers.length} member{activeMembers.length !== 1 ? "s" : ""}
                            </p>
                        </div>
                    ) : (
                        <h2 style={{ color: "#FFFFFF", fontSize: 22, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
                            {group.name}
                        </h2>
                    )}
                    <p style={{ color: "rgba(255,255,255,0.75)", fontSize: 14, fontFamily: "DM Sans, sans-serif", marginTop: 2 }}>
                        {cfg.label} · {activeMembers.length} member{activeMembers.length !== 1 ? "s" : ""}
                    </p>
                </div>

                {/* Edit / Save / Cancel buttons */}
                {isAdmin && !editingGroup && (
                    <button
                        onClick={startEditing}
                        className="flex items-center justify-center flex-shrink-0"
                        style={{ width: 34, height: 34, borderRadius: 10, backgroundColor: "#FFFFFF" }}
                    >
                        <Pencil className="w-4 h-4" style={{ color: cfg.bg }} />
                    </button>
                )}
                {editingGroup && (
                    <div className="flex gap-2 flex-shrink-0">
                        <button
                            onClick={handleSaveGroup}
                            disabled={saving}
                            className="flex items-center justify-center"
                            style={{ width: 34, height: 34, borderRadius: 10, backgroundColor: "#FFFFFF" }}
                        >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: cfg.bg }} /> : <Check className="w-4 h-4" style={{ color: cfg.bg }} />}
                        </button>
                        <button
                            onClick={() => { setEditingGroup(false); setShowEmojiPicker(false); }}
                            className="flex items-center justify-center"
                            style={{ width: 34, height: 34, borderRadius: 10, backgroundColor: "rgba(255,255,255,0.15)" }}
                        >
                            <X className="w-4 h-4" style={{ color: "#FFFFFF" }} />
                        </button>
                    </div>
                )}
            </div>

            {/* Emoji picker (visible during edit) */}
            {editingGroup && showEmojiPicker && (
                <div style={{
                    marginBottom: 12, padding: 12, borderRadius: 14,
                    backgroundColor: "#FFFFFF",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 4 }}>
                        {GROUP_EMOJIS.map((emoji) => (
                            <button
                                key={emoji}
                                onClick={() => { setEditEmoji(emoji); setShowEmojiPicker(false); }}
                                className="flex items-center justify-center"
                                style={{
                                    height: 36, borderRadius: 8, fontSize: 18,
                                    backgroundColor: editEmoji === emoji ? "#EEF2FF" : "transparent",
                                    border: editEmoji === emoji ? `2px solid ${PRIMARY}` : "2px solid transparent",
                                }}
                            >
                                {emoji}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Stats row */}
            <div className="flex gap-2" style={{ marginBottom: 16 }}>
                {[
                    { value: sharedCounts.receipts, label: "Shared\nreceipts" },
                    { value: sharedCounts.cards, label: "Shared\ncards" },
                    { value: activeMembers.length, label: "Members" },
                ].map((stat, i) => (
                    <div
                        key={i}
                        className="flex-1"
                        style={{
                            padding: "12px 14px",
                            borderRadius: 14,
                            backgroundColor: "rgba(255,255,255,0.15)",
                        }}
                    >
                        <p style={{ color: "#FFFFFF", fontSize: 22, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
                            {stat.value}
                        </p>
                        <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 12, fontFamily: "DM Sans, sans-serif", marginTop: 2, whiteSpace: "pre-line", lineHeight: 1.3 }}>
                            {stat.label}
                        </p>
                    </div>
                ))}
            </div>

            {/* Open this circle button */}
            <button
                onClick={() => navigate(`/groups/${groupId}/view`)}
                className="w-full flex items-center justify-center gap-2"
                style={{
                    height: 46, borderRadius: 14,
                    backgroundColor: "#FFFFFF", color: "#111827",
                    fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif",
                }}
            >
                <LineChart className="w-4 h-4" />
                Open this circle
            </button>
        </>
    );

    // ── Members section ──────────────────────────────────────────────────────
    const membersContent = () => (
        <div>
            {/* Closed circle banner */}
            {group?.status === "closed" && (
                <div className="flex items-center gap-3 mb-4" style={{
                    padding: "12px 16px", borderRadius: 14,
                    backgroundColor: "#FFFBEB", border: "1.5px solid #FDE68A",
                }}>
                    <Lock className="w-4 h-4 flex-shrink-0" style={{ color: "#D97706" }} />
                    <div className="flex-1 min-w-0">
                        <p style={{ color: "#92400E", fontSize: 13, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>Circle closed</p>
                        <p style={{ color: "#92400E", fontSize: 12, fontFamily: "DM Sans, sans-serif", marginTop: 2 }}>
                            {isAdmin
                                ? "History preserved. Only you can reopen or delete this circle."
                                : `Closed by ${group.closed_by_name || "the owner"} · Only they can reopen it.`}
                        </p>
                    </div>
                    <button
                        onClick={() => navigate(`/groups/${groupId}/summary`)}
                        style={{ color: PRIMARY, fontSize: 13, fontWeight: 700, fontFamily: "DM Sans, sans-serif", whiteSpace: "nowrap", flexShrink: 0 }}
                    >
                        Summary →
                    </button>
                </div>
            )}

            {/* Section header */}
            <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
                <h3 style={{ color: "#111827", fontSize: 16, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
                    Members
                </h3>
                {group?.status !== "closed" && (
                    <button
                        onClick={() => setShowInvite(!showInvite)}
                        className="flex items-center gap-1.5"
                        style={{ color: PRIMARY, fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}
                    >
                        <UserPlus className="w-4 h-4" />
                        Add member
                    </button>
                )}
            </div>

            {/* Add member form — name required, email optional */}
            {showInvite && group?.status !== "closed" && (
                <div className="mb-4" style={{ backgroundColor: "#F8FAFF", borderRadius: 14, border: "1px solid #E5E7EB", padding: "14px 14px 10px" }}>
                    <p style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", fontFamily: "DM Sans, sans-serif", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.5px" }}>Add member</p>
                    {/* Name row */}
                    <div className="flex items-center gap-2 mb-2">
                        <div className="flex-1 flex items-center" style={{
                            height: 44, borderRadius: 10, border: "1.5px solid #E5E7EB",
                            backgroundColor: "#fff", paddingLeft: 12, paddingRight: 8,
                        }}>
                            <input
                                type="text"
                                value={inviteName}
                                onChange={(e) => setInviteName(e.target.value)}
                                placeholder="Name (required)"
                                autoFocus
                                className="flex-1 bg-transparent"
                                style={{ fontSize: 16, fontFamily: "DM Sans, sans-serif", color: "#111827", outline: "none", border: "none" }}
                            />
                        </div>
                    </div>
                    {/* Email row (optional) */}
                    <div className="flex items-center gap-2 mb-3">
                        <div className="flex-1 flex items-center" style={{
                            height: 44, borderRadius: 10, border: "1.5px solid #E5E7EB",
                            backgroundColor: "#fff", paddingLeft: 12, paddingRight: 8,
                        }}>
                            <Mail className="w-4 h-4 flex-shrink-0" style={{ color: "#9CA3AF", marginRight: 8 }} />
                            <input
                                type="email"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddMember(); } }}
                                placeholder="Email (optional)"
                                className="flex-1 bg-transparent"
                                style={{ fontSize: 16, fontFamily: "DM Sans, sans-serif", color: "#111827", outline: "none", border: "none" }}
                            />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={handleAddMember}
                            disabled={addingMember || !inviteName.trim()}
                            className="flex items-center justify-center gap-2"
                            style={{
                                flex: 1, height: 40, borderRadius: 10, background: addingMember || !inviteName.trim() ? "#D1D5DB" : "linear-gradient(135deg, #2D5BFF, #1B3DBF)",
                                color: "#fff", fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif", border: "none", cursor: "pointer",
                            }}
                        >
                            {addingMember ? <Loader2 className="w-4 h-4 animate-spin" /> : (inviteEmail.trim() ? "Send invite" : "Add guest")}
                        </button>
                        <button
                            onClick={() => { setShowInvite(false); setInviteName(""); setInviteEmail(""); }}
                            className="flex items-center justify-center flex-shrink-0"
                            style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: "#F3F4F6", border: "none", cursor: "pointer" }}
                        >
                            <X className="w-4 h-4" style={{ color: "#6B7280" }} />
                        </button>
                    </div>
                    <p style={{ color: "#9CA3AF", fontSize: 11, fontFamily: "DM Sans, sans-serif", marginTop: 8 }}>
                        {inviteEmail.trim() ? "They'll receive an email invite to join via Google" : "Guest is added immediately — you can add their email later"}
                    </p>
                </div>
            )}
            {/* Members card */}
            <div style={{
                backgroundColor: "#FFFFFF", borderRadius: 20, padding: "4px 18px",
                boxShadow: "0 4px 16px rgba(15,23,42,0.06)",
            }}>
                {/* Active members */}
                {activeMembers.map((m, idx) => {
                    const isMe = m.user_id === authUser?.id;
                    const isOwner = m.role === "admin";
                    const name = m.display_name || m.email || "Unknown";
                    const email = m.email || "";
                    const initials = name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
                    const colors = ["#7C3AED", "#2563EB", "#0D9488", "#D97706", "#DC2626", "#4F46E5", "#059669"];
                    const colorIdx = (name.charCodeAt(0) + (name.charCodeAt(1) || 0)) % colors.length;
                    const hasBorder = idx < activeMembers.length + pendingMembers.length - 1;

                    return (
                        <div
                            key={m.id}
                            className="flex items-center gap-3 py-3"
                            style={{ borderBottom: hasBorder ? "1px solid #F3F4F6" : "none" }}
                        >
                            <div
                                className="flex items-center justify-center overflow-hidden flex-shrink-0"
                                style={{
                                    width: 40, height: 40, borderRadius: 999,
                                    backgroundColor: m.avatar_url ? "transparent" : colors[colorIdx],
                                    color: "#FFFFFF", fontWeight: 700, fontSize: 14, fontFamily: "Manrope, sans-serif",
                                }}
                            >
                                {m.avatar_url ? (
                                    <img src={m.avatar_url} alt={name} className="w-full h-full object-cover" />
                                ) : initials}
                            </div>
                            <div className="flex-1 min-w-0">
                                {/* Line 1: Name + crown */}
                                <div className="flex items-center gap-1.5">
                                    <p className="truncate" style={{ color: "#111827", fontSize: 15, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>
                                        {name}{isMe ? " (You)" : ""}
                                    </p>
                                    {isOwner && <Crown className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#D97706" }} />}
                                </div>
                                {/* Line 2: Email or Add email for placeholder */}
                                {m.is_placeholder ? (
                                    addEmailFor === m.user_id ? (
                                        <div className="flex items-center gap-1.5 mt-1">
                                            <input
                                                type="email"
                                                value={addEmailValue}
                                                onChange={e => setAddEmailValue(e.target.value)}
                                                onKeyDown={e => { if (e.key === "Enter") handleAddEmail(m.user_id); if (e.key === "Escape") setAddEmailFor(null); }}
                                                placeholder="their@email.com"
                                                autoFocus
                                                style={{ fontSize: 16, fontFamily: "DM Sans, sans-serif", border: "1px solid #D1D5DB", borderRadius: 6, padding: "3px 8px", outline: "none", flex: 1, minWidth: 0 }}
                                            />
                                            <button onClick={() => handleAddEmail(m.user_id)} disabled={addingEmail} style={{ fontSize: 12, fontWeight: 700, color: "#fff", backgroundColor: PRIMARY, border: "none", borderRadius: 6, padding: "4px 10px", cursor: "pointer", flexShrink: 0 }}>
                                                {addingEmail ? "…" : "Save"}
                                            </button>
                                            <button onClick={() => { setAddEmailFor(null); setAddEmailValue(""); }} style={{ fontSize: 11, color: "#9CA3AF", background: "none", border: "none", cursor: "pointer", flexShrink: 0 }}>✕</button>
                                        </div>
                                    ) : (
                                        <button onClick={() => { setAddEmailFor(m.user_id); setAddEmailValue(""); }} style={{ fontSize: 12, color: PRIMARY, fontFamily: "DM Sans, sans-serif", background: "none", border: "none", padding: 0, cursor: "pointer", marginTop: 2 }}>
                                            + Add email
                                        </button>
                                    )
                                ) : email ? (
                                    <p style={{ color: "#9CA3AF", fontSize: 12, fontFamily: "DM Sans, sans-serif", marginTop: 1 }}>{email}</p>
                                ) : null}
                                {/* Line 3: Status */}
                                <div className="flex items-center gap-1.5 mt-1">
                                    {m.is_placeholder ? (
                                        <>
                                            <span style={{ width: 6, height: 6, borderRadius: 999, backgroundColor: "#9CA3AF", display: "inline-block", flexShrink: 0 }} />
                                            <span style={{ color: "#9CA3AF", fontSize: 11, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>Guest</span>
                                        </>
                                    ) : (
                                        <>
                                            <span style={{ width: 6, height: 6, borderRadius: 999, backgroundColor: "#16A34A", display: "inline-block", flexShrink: 0 }} />
                                            <span style={{ color: "#16A34A", fontSize: 11, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>Active</span>
                                        </>
                                    )}
                                </div>
                            </div>
                            {isAdmin && !isMe && (
                                <button
                                    onClick={() => handleRemoveMember(m.id, name)}
                                    className="flex items-center justify-center flex-shrink-0"
                                    style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: "#FEE2E2" }}
                                >
                                    <X className="w-3.5 h-3.5" style={{ color: "#EF4444" }} />
                                </button>
                            )}
                        </div>
                    );
                })}

                {/* Pending members */}
                {pendingMembers.map((m, idx) => {
                    const email = m.email || "";
                    const displayName = m.display_name || email || "Pending";
                    const initials = displayName.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
                    const colors = ["#7C3AED", "#2563EB", "#0D9488", "#D97706", "#DC2626", "#4F46E5", "#059669"];
                    const colorIdx = (displayName.charCodeAt(0) + (displayName.charCodeAt(1) || 0)) % colors.length;
                    const isResending = resending === m.id;

                    return (
                        <div
                            key={m.id}
                            className="flex items-start gap-3 py-3"
                            style={{ borderBottom: idx < pendingMembers.length - 1 ? "1px solid #F3F4F6" : "none" }}
                        >
                            <div
                                className="flex items-center justify-center overflow-hidden flex-shrink-0 mt-0.5"
                                style={{
                                    width: 40, height: 40, borderRadius: 999,
                                    backgroundColor: colors[colorIdx],
                                    color: "#FFFFFF", fontWeight: 700, fontSize: 14, fontFamily: "Manrope, sans-serif",
                                    opacity: 0.6,
                                }}
                            >
                                {initials}
                            </div>
                            <div className="flex-1 min-w-0">
                                {/* Line 1: Name (if known, else email) */}
                                <p style={{ color: "#111827", fontSize: 15, fontWeight: 600, fontFamily: "Manrope, sans-serif", lineHeight: 1.3 }}>
                                    {m.display_name || email}
                                </p>
                                {/* Line 2: Email (if name shown separately) */}
                                {m.display_name && email && (
                                    <p style={{ color: "#9CA3AF", fontSize: 12, fontFamily: "DM Sans, sans-serif", marginTop: 1, wordBreak: "break-all" }}>
                                        {email}
                                    </p>
                                )}
                                {/* Line 3: Pending status + Resend */}
                                <div className="flex items-center gap-3 mt-1.5">
                                    <div className="flex items-center gap-1.5">
                                        <span style={{ width: 6, height: 6, borderRadius: 999, backgroundColor: "#D97706", display: "inline-block", flexShrink: 0 }} />
                                        <span style={{ color: "#D97706", fontSize: 11, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>Pending</span>
                                    </div>
                                    {isAdmin && (
                                        <div>
                                            <button
                                                onClick={() => handleResendInvite(m.id, email)}
                                                disabled={isResending}
                                                className="flex items-center gap-1"
                                                style={{ color: PRIMARY, fontSize: 12, fontWeight: 600, fontFamily: "DM Sans, sans-serif", opacity: isResending ? 0.6 : 1 }}
                                            >
                                                {isResending ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                                                {isResending ? "Sending…" : "Resend email"}
                                            </button>
                                            {resendBlocked === m.id && (
                                                <p style={{ color: "#D97706", fontSize: 11, fontFamily: "DM Sans, sans-serif", marginTop: 3 }}>
                                                    Already sent today — try again tomorrow
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            {isAdmin && (
                                <button
                                    onClick={() => handleCancelInvite(m.id)}
                                    className="flex items-center justify-center flex-shrink-0 mt-1"
                                    style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: "#FEE2E2" }}
                                    title="Cancel invite"
                                >
                                    <X className="w-3.5 h-3.5" style={{ color: "#EF4444" }} />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Leave circle — non-admin members only, active circles only */}
            {!isAdmin && group?.status !== "closed" && (
            <div style={{ marginTop: 24 }}>
                {!confirmLeave ? (
                    <button
                        onClick={() => setConfirmLeave(true)}
                        className="w-full flex items-center justify-center gap-2"
                        style={{
                            height: 48, borderRadius: 14,
                            border: "1.5px solid #FECACA",
                            backgroundColor: "#FFFFFF",
                            color: "#EF4444", fontSize: 15, fontWeight: 600,
                            fontFamily: "DM Sans, sans-serif",
                        }}
                    >
                        <LogOut className="w-4 h-4" />
                        Leave circle
                    </button>
                ) : (
                    <div style={{
                        padding: 16, borderRadius: 14,
                        border: "1.5px solid #FECACA", backgroundColor: "#FEF2F2",
                    }}>
                        <div className="flex items-center gap-2 mb-3">
                            <AlertTriangle className="w-4 h-4 flex-shrink-0" style={{ color: "#EF4444" }} />
                            <p style={{ color: "#EF4444", fontSize: 13, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>
                                {activeMembers.length === 1
                                    ? "You're the last member. The circle will be deleted."
                                    : "Your shared items will be removed from this circle."}
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setConfirmLeave(false)}
                                className="flex-1 py-2.5 rounded-xl"
                                style={{ border: "1px solid #E5E7EB", color: "#374151", fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleLeave}
                                disabled={leaving}
                                className="flex-1 py-2.5 rounded-xl flex items-center justify-center gap-2"
                                style={{ backgroundColor: "#EF4444", color: "#FFFFFF", fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}
                            >
                                {leaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
                                {leaving ? "Leaving…" : "Yes, leave"}
                            </button>
                        </div>
                    </div>
                )}
            </div>
            )}

            {/* Reimbursement tracking — any member, active circles only */}
            {group?.status !== "closed" && !group?.reimbursement_tracking_enabled && (
                <div style={{ marginTop: 12 }}>
                    <button
                        onClick={() => setShowEnableReimb(true)}
                        className="w-full flex items-center gap-3 rounded-2xl"
                        style={{ backgroundColor: "#F0FDFA", border: "1px solid #99F6E4", padding: "14px 16px", cursor: "pointer", textAlign: "left" }}
                    >
                        <div style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: "#CCFBF1", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <ArrowLeftRight className="w-4 h-4" style={{ color: "#0D9488" }} />
                        </div>
                        <div className="flex-1">
                            <p style={{ fontSize: 14, fontWeight: 700, color: "#065F46", fontFamily: "Manrope, sans-serif" }}>Enable reimbursement tracking</p>
                            <p style={{ fontSize: 12, color: "#0D9488", fontFamily: "DM Sans, sans-serif", marginTop: 1 }}>Track who paid and when you're paid back</p>
                        </div>
                        <span style={{ fontSize: 18, color: "#0D9488" }}>›</span>
                    </button>
                </div>
            )}

            {group?.reimbursement_tracking_enabled && (
                <div style={{ marginTop: 12 }}>
                    {/* Header */}
                    <div style={{ backgroundColor: "#F0FDFA", border: "1px solid #99F6E4", borderRadius: "14px 14px 0 0", padding: "12px 16px", display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: "#CCFBF1", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <ArrowLeftRight className="w-4 h-4" style={{ color: "#0D9488" }} />
                        </div>
                        <div className="flex-1">
                            <p style={{ fontSize: 13, fontWeight: 700, color: "#065F46", fontFamily: "Manrope, sans-serif" }}>Reimbursement tracking active</p>
                            <p style={{ fontSize: 11, color: "#0D9488", fontFamily: "DM Sans, sans-serif" }}>Payment sources &amp; default set below</p>
                        </div>
                    </div>

                    {/* Default reimbursed by */}
                    <div style={{ backgroundColor: "#fff", border: "1px solid #E5E7EB", borderTop: "none", padding: "12px 16px" }}>
                        <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#6B7280", marginBottom: 8, fontFamily: "DM Sans, sans-serif" }}>Default reimbursed by</p>
                        {(() => {
                            const defaultName = group?.default_reimb_name || group?.default_reimb_label_name;
                            return defaultName ? (
                                <div className="flex items-center gap-3">
                                    <div style={{ width: 28, height: 28, borderRadius: "50%", backgroundColor: group?.default_reimb_label_name ? "#0D9488" : "#2D5BFF", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                                        {group?.default_reimb_label_name ? "🏦" : defaultName.charAt(0)}
                                    </div>
                                    <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: "#111827", fontFamily: "DM Sans, sans-serif" }}>{defaultName}</span>
                                    <button onClick={() => handleUpdateDefault(null, null, null)} style={{ fontSize: 12, color: "#9CA3AF", background: "none", border: "none", cursor: "pointer", fontFamily: "DM Sans, sans-serif" }}>Clear</button>
                                </div>
                            ) : (
                                <p style={{ fontSize: 13, color: "#9CA3AF", fontFamily: "DM Sans, sans-serif" }}>Not set — choose from members or labels below</p>
                            );
                        })()}
                        {/* Members as default options */}
                        <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {members.filter(m => m.status === 'active' || !m.status).map(m => {
                                const name = m.display_name || m.email || 'Unknown';
                                const id = m.user_id || m.email;
                                const isCurrent = group?.default_reimb_user_id === id;
                                return (
                                    <button key={id} onClick={() => handleUpdateDefault(id, name, null)}
                                        style={{ padding: "4px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, fontFamily: "DM Sans, sans-serif", cursor: "pointer", border: `1.5px solid ${isCurrent ? "#2D5BFF" : "#E5E7EB"}`, backgroundColor: isCurrent ? "#EFF6FF" : "#F9FAFB", color: isCurrent ? "#2D5BFF" : "#374151" }}>
                                        {name}{m.user_id === authUser?.id ? " (You)" : ""}
                                    </button>
                                );
                            })}
                            {circleLabels.map(lbl => {
                                const isCurrent = group?.default_reimb_label_name === lbl.name;
                                return (
                                    <button key={lbl.id} onClick={() => handleUpdateDefault(null, null, lbl.name)}
                                        style={{ padding: "4px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, fontFamily: "DM Sans, sans-serif", cursor: "pointer", border: `1.5px solid ${isCurrent ? "#0D9488" : "#E5E7EB"}`, backgroundColor: isCurrent ? "#F0FDFA" : "#F9FAFB", color: isCurrent ? "#0D9488" : "#374151" }}>
                                        🏦 {lbl.name}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Payment labels list */}
                    <div style={{ backgroundColor: "#fff", border: "1px solid #E5E7EB", borderTop: "none", padding: "12px 16px", borderRadius: "0 0 14px 14px" }}>
                        <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#6B7280", marginBottom: 8, fontFamily: "DM Sans, sans-serif" }}>Payment labels</p>
                        {circleLabels.length === 0 && !showAddPaymentLabel && (
                            <p style={{ fontSize: 13, color: "#9CA3AF", fontFamily: "DM Sans, sans-serif", marginBottom: 8 }}>No labels yet — add a shared account or common cash pot</p>
                        )}
                        {circleLabels.map(lbl => (
                            <div key={lbl.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: "1px solid #F3F4F6" }}>
                                <div style={{ width: 28, height: 28, borderRadius: 7, backgroundColor: "#0D9488", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>🏦</div>
                                {editingLabelId === lbl.id ? (
                                    <div className="flex gap-2 flex-1">
                                        <input
                                            type="text"
                                            value={editingLabelName}
                                            onChange={e => setEditingLabelName(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') handleRenameLabel(lbl.id); if (e.key === 'Escape') setEditingLabelId(null); }}
                                            autoFocus
                                            style={{ flex: 1, fontSize: 16, fontFamily: "DM Sans, sans-serif", border: "1px solid #2D5BFF", borderRadius: 7, padding: "4px 8px", outline: "none" }}
                                        />
                                        <button onClick={() => handleRenameLabel(lbl.id)} disabled={savingLabelRename} style={{ fontSize: 12, fontWeight: 700, color: "#fff", backgroundColor: "#2D5BFF", border: "none", borderRadius: 7, padding: "4px 10px", cursor: "pointer" }}>
                                            {savingLabelRename ? "…" : "Save"}
                                        </button>
                                        <button onClick={() => setEditingLabelId(null)} style={{ fontSize: 12, color: "#9CA3AF", background: "none", border: "none", cursor: "pointer" }}>✕</button>
                                    </div>
                                ) : (
                                    <>
                                        <span style={{ flex: 1, fontSize: 14, color: "#111827", fontFamily: "DM Sans, sans-serif" }}>{lbl.name}</span>
                                        <button onClick={() => { setEditingLabelId(lbl.id); setEditingLabelName(lbl.name); }} style={{ fontSize: 12, fontWeight: 600, color: "#2D5BFF", background: "none", border: "none", cursor: "pointer", fontFamily: "DM Sans, sans-serif" }}>Rename</button>
                                    </>
                                )}
                            </div>
                        ))}

                        {/* Add new label */}
                        {showAddPaymentLabel ? (
                            <div style={{ marginTop: 8 }}>
                                <input
                                    type="text"
                                    placeholder="e.g. Bank Account, Family Pot"
                                    value={newPaymentLabelName}
                                    onChange={e => setNewPaymentLabelName(e.target.value)}
                                    style={{ width: "100%", fontSize: 16, fontFamily: "DM Sans, sans-serif", border: "1px solid #D1D5DB", borderRadius: 8, padding: "8px 12px", outline: "none", marginBottom: 6 }}
                                />
                                <div className="flex gap-2 mb-2">
                                    {[['bank', 'Bank account'], ['cash', 'Cash / pot'], ['person', 'Person']].map(([t, label]) => (
                                        <button key={t} onClick={() => setNewPaymentLabelType(t)} style={{ flex: 1, padding: "5px 4px", borderRadius: 7, border: `1px solid ${newPaymentLabelType === t ? "#2D5BFF" : "#E5E7EB"}`, backgroundColor: newPaymentLabelType === t ? "#EFF6FF" : "#fff", color: newPaymentLabelType === t ? "#2D5BFF" : "#6B7280", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "DM Sans, sans-serif" }}>
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={handleAddPaymentLabel} disabled={addingPaymentLabel || !newPaymentLabelName.trim()} style={{ flex: 1, backgroundColor: "#2D5BFF", borderRadius: 8, padding: "8px", color: "#fff", fontSize: 13, fontWeight: 700, border: "none", cursor: "pointer", opacity: addingPaymentLabel || !newPaymentLabelName.trim() ? 0.6 : 1, fontFamily: "DM Sans, sans-serif" }}>
                                        {addingPaymentLabel ? "Adding…" : "Add"}
                                    </button>
                                    <button onClick={() => { setShowAddPaymentLabel(false); setNewPaymentLabelName(''); }} style={{ flex: 1, backgroundColor: "#F3F4F6", borderRadius: 8, padding: "8px", color: "#374151", fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "DM Sans, sans-serif" }}>Cancel</button>
                                </div>
                            </div>
                        ) : (
                            <button onClick={() => setShowAddPaymentLabel(true)} style={{ marginTop: 8, fontSize: 13, fontWeight: 600, color: "#2D5BFF", background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "DM Sans, sans-serif", display: "flex", alignItems: "center", gap: 4 }}>
                                <Plus className="w-4 h-4" /> Add payment source
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Enable reimbursement confirmation */}
            {showEnableReimb && (
                <div className="fixed inset-0" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 300, display: "flex", alignItems: "flex-end" }}>
                    <div style={{ backgroundColor: "#fff", borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: "20px 20px 40px", width: "100%", maxHeight: "85vh", overflowY: "auto" }}>
                        <div className="flex justify-center mb-4"><div style={{ width: 36, height: 4, borderRadius: 999, backgroundColor: "#D1D5DB" }} /></div>

                        {/* Hero */}
                        <div style={{ background: "linear-gradient(135deg, #2D5BFF, #1B3DBF)", borderRadius: 16, padding: 20, marginBottom: 16, textAlign: "center" }}>
                            <div style={{ width: 52, height: 52, borderRadius: 14, backgroundColor: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}>
                            <ArrowLeftRight className="w-6 h-6" style={{ color: "#fff" }} />
                        </div>
                            <p style={{ fontSize: 18, fontWeight: 700, color: "#fff", fontFamily: "Manrope, sans-serif", marginBottom: 4 }}>Reimbursement tracking</p>
                            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.8)", fontFamily: "DM Sans, sans-serif", lineHeight: 1.5 }}>Track who paid for what and when money is returned — across your circle</p>
                        </div>

                        {/* Feature list */}
                        <div style={{ backgroundColor: "#fff", border: "1px solid #E5E7EB", borderRadius: 14, overflow: "hidden", marginBottom: 12 }}>
                            {[
                                { icon: "👤", title: "Who paid?", sub: "Added to every new shared receipt" },
                                { icon: "🚩", title: "Pending tracking", sub: "Flags receipts where money hasn't come back" },
                                { icon: "🏦", title: "Custom labels", sub: "e.g. Bank Account, Common Cash" },
                                { icon: "↩️", title: "Always reversible", sub: "Any receipt can be updated or undone" },
                            ].map((f, i, arr) => (
                                <div key={f.title} className="flex items-start gap-3" style={{ padding: "12px 14px", borderBottom: i < arr.length - 1 ? "1px solid #F3F4F6" : "none" }}>
                                    <div style={{ width: 30, height: 30, borderRadius: 8, backgroundColor: "#EFF6FF", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>{f.icon}</div>
                                    <div><p style={{ fontSize: 13, fontWeight: 700, color: "#111827", fontFamily: "Manrope, sans-serif" }}>{f.title}</p><p style={{ fontSize: 12, color: "#6B7280", fontFamily: "DM Sans, sans-serif", marginTop: 1 }}>{f.sub}</p></div>
                                </div>
                            ))}
                        </div>

                        {/* Warning */}
                        <div style={{ backgroundColor: "#FFFBEB", border: "1px solid #FCD34D", borderRadius: 12, padding: "12px 14px", marginBottom: 16, display: "flex", alignItems: "flex-start", gap: 10 }}>
                            <span style={{ fontSize: 18, flexShrink: 0 }}>⚠️</span>
                            <div>
                                <p style={{ fontSize: 13, color: "#92400E", fontFamily: "DM Sans, sans-serif", lineHeight: 1.5 }}>
                                    <strong>All existing shared receipts</strong> will be set to "Paid by the uploader, pending reimbursement" by default. Anyone can update them.<br /><br />
                                    <strong>This cannot be undone</strong> — once enabled, reimbursement tracking is permanent for this circle.
                                </p>
                            </div>
                        </div>

                        <button
                            onClick={handleEnableReimbursement}
                            disabled={enablingReimb}
                            className="w-full flex items-center justify-center gap-2"
                            style={{ background: enablingReimb ? "#93C5FD" : "linear-gradient(135deg, #2D5BFF, #1B3DBF)", borderRadius: 14, padding: 14, color: "#fff", fontSize: 15, fontWeight: 700, fontFamily: "DM Sans, sans-serif", border: "none", cursor: "pointer", marginBottom: 10 }}
                        >
                            {enablingReimb ? "Enabling…" : "Enable reimbursement tracking"}
                        </button>
                        <button
                            onClick={() => setShowEnableReimb(false)}
                            className="w-full"
                            style={{ backgroundColor: "transparent", border: "none", padding: 12, color: "#9CA3AF", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
                        >
                            Maybe later
                        </button>
                    </div>
                </div>
            )}

            {/* Close circle (admin, active only) */}
            {isAdmin && group?.status !== "closed" && (
                <div style={{ marginTop: 12 }}>
                    {!confirmClose ? (
                        <button
                            onClick={() => setConfirmClose(true)}
                            className="w-full flex items-center justify-center gap-2"
                            style={{
                                height: 48, borderRadius: 14,
                                backgroundColor: "transparent",
                                color: "#D97706", fontSize: 14, fontWeight: 600,
                                fontFamily: "DM Sans, sans-serif",
                            }}
                        >
                            <Lock className="w-4 h-4" />
                            Close circle
                        </button>
                    ) : (
                        <div style={{ padding: 16, borderRadius: 14, border: "1.5px solid #FDE68A", backgroundColor: "#FFFBEB" }}>
                            <div className="flex items-start gap-2 mb-3">
                                <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#D97706" }} />
                                <p style={{ color: "#92400E", fontSize: 13, fontWeight: 600, fontFamily: "DM Sans, sans-serif", lineHeight: 1.5 }}>
                                    Closing the circle keeps all shared history safe. Members won't be able to add new items. You can reopen it anytime.
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => setConfirmClose(false)} className="flex-1 py-2.5 rounded-xl"
                                    style={{ border: "1px solid #E5E7EB", color: "#374151", fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>
                                    Cancel
                                </button>
                                <button onClick={handleClose} disabled={closing}
                                    className="flex-1 py-2.5 rounded-xl flex items-center justify-center gap-2"
                                    style={{ backgroundColor: "#D97706", color: "#FFFFFF", fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>
                                    {closing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                                    {closing ? "Closing…" : "Yes, close"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Closed circle actions — admin only */}
            {isAdmin && group?.status === "closed" && (
                <div style={{ marginTop: 12 }}>
                    {/* Summary button */}
                    <button
                        onClick={() => navigate(`/groups/${groupId}/summary`)}
                        className="w-full flex items-center justify-center gap-2 mb-3"
                        style={{
                            height: 48, borderRadius: 14,
                            backgroundColor: "#EEF2FF",
                            color: PRIMARY, fontSize: 15, fontWeight: 600,
                            fontFamily: "DM Sans, sans-serif",
                        }}
                    >
                        <ClipboardList className="w-4 h-4" />
                        View circle summary
                    </button>

                    {/* Reopen */}
                    {!confirmReopen ? (
                        <button onClick={() => setConfirmReopen(true)}
                            className="w-full flex items-center justify-center gap-2 mb-2"
                            style={{ height: 48, borderRadius: 14, border: "1.5px solid #BFDBFE", backgroundColor: "#FFFFFF", color: PRIMARY, fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>
                            <RefreshCw className="w-4 h-4" />
                            Reopen circle
                        </button>
                    ) : (
                        <div style={{ padding: 16, borderRadius: 14, border: "1.5px solid #BFDBFE", backgroundColor: "#EFF6FF", marginBottom: 8 }}>
                            <p style={{ color: "#1E40AF", fontSize: 13, fontWeight: 600, fontFamily: "DM Sans, sans-serif", marginBottom: 10 }}>
                                Reopen this circle so members can share items again?
                            </p>
                            <div className="flex gap-2">
                                <button onClick={() => setConfirmReopen(false)} className="flex-1 py-2.5 rounded-xl"
                                    style={{ border: "1px solid #E5E7EB", color: "#374151", fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>
                                    Cancel
                                </button>
                                <button onClick={handleReopen} disabled={reopening}
                                    className="flex-1 py-2.5 rounded-xl flex items-center justify-center gap-2"
                                    style={{ backgroundColor: PRIMARY, color: "#FFFFFF", fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>
                                    {reopening ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                                    {reopening ? "Reopening…" : "Yes, reopen"}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Permanently delete */}
                    {!confirmPermanentDelete ? (
                        <button onClick={() => setConfirmPermanentDelete(true)}
                            className="w-full flex items-center justify-center gap-2"
                            style={{ height: 48, borderRadius: 14, backgroundColor: "transparent", color: "#EF4444", fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>
                            <ArchiveX className="w-4 h-4" />
                            Permanently delete
                        </button>
                    ) : (
                        <div style={{ padding: 16, borderRadius: 14, border: "1.5px solid #FECACA", backgroundColor: "#FEF2F2" }}>
                            <div className="flex items-start gap-2 mb-3">
                                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#EF4444" }} />
                                <p style={{ color: "#EF4444", fontSize: 13, fontWeight: 600, fontFamily: "DM Sans, sans-serif", lineHeight: 1.5 }}>
                                    This will permanently delete the circle and all shared history. This cannot be undone.
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => setConfirmPermanentDelete(false)} className="flex-1 py-2.5 rounded-xl"
                                    style={{ border: "1px solid #E5E7EB", color: "#374151", fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>
                                    Cancel
                                </button>
                                <button onClick={handlePermanentDelete} disabled={permanentDeleting}
                                    className="flex-1 py-2.5 rounded-xl flex items-center justify-center gap-2"
                                    style={{ backgroundColor: "#EF4444", color: "#FFFFFF", fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>
                                    {permanentDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArchiveX className="w-4 h-4" />}
                                    {permanentDeleting ? "Deleting…" : "Yes, permanently delete"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    // ── MOBILE LAYOUT ────────────────────────────────────────────────────────
    if (isMobile) {
        return (
            <div className="min-h-screen flex flex-col" style={{ backgroundColor: "#F9FAFB" }}>
                <header style={{ background: GRADIENT, padding: "16px 20px 24px 20px", borderBottomLeftRadius: 28, borderBottomRightRadius: 28 }}>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center justify-center"
                            style={{ width: 36, height: 36, borderRadius: 999, backgroundColor: "rgba(255,255,255,0.15)" }}
                        >
                            <ChevronLeft className="w-5 h-5" style={{ color: "#FFFFFF" }} />
                        </button>
                        <h1 style={{ color: "#FFFFFF", fontSize: 20, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
                            Group
                        </h1>
                    </div>
                    {headerContent()}
                </header>
                <div className="flex-1 px-4 pb-10" style={{ marginTop: 20 }}>
                    {membersContent()}
                </div>
            </div>
        );
    }

    // ── DESKTOP LAYOUT ───────────────────────────────────────────────────────
    const userName = `${authUser?.first_name || ""} ${authUser?.last_name || ""}`.trim() || "User";
    const avatarUrl = authUser?.avatar_url;
    const email = authUser?.email || "";

    return (
        <div className="min-h-screen flex" style={{ backgroundColor: "#F3F4F6" }}>
            <aside className="flex flex-col" style={{ width: 240, backgroundColor: "#F9FAFB", borderRight: "1px solid #F3F4F6" }}>
                <div className="flex items-center gap-2 px-5 pt-6 pb-8">
                    <svg width={28} height={28} viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" style={{ borderRadius: 6, flexShrink: 0 }}>
                        <defs><linearGradient id="sb-g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#4DD4E6" /><stop offset="100%" stopColor="#1B6CF5" /></linearGradient></defs>
                        <rect width="200" height="200" rx="44" fill="url(#sb-g)" />
                        <rect x="78" y="8" width="44" height="22" rx="8" fill="#3BBBD0" />
                        <circle cx="100" cy="95" r="38" fill="#FFF" />
                        <circle cx="106" cy="90" r="22" fill="#0F1B4D" />
                        <circle cx="114" cy="82" r="5" fill="#FFF" />
                        <path d="M72 140 Q100 164 128 140" stroke="#0F1B4D" strokeWidth="6" fill="none" strokeLinecap="round" />
                    </svg>
                    <span style={{ color: "#111827", fontSize: 18, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>My Buddy</span>
                </div>
                <nav className="flex-1 px-3">
                    {NAV_ITEMS.map((item) => (
                        <button key={item.key} onClick={() => onNavClick(item.key)} className="w-full flex items-center gap-3 px-4 py-3 mb-1 transition-all" style={{ backgroundColor: "transparent", color: "#6B7280", borderRadius: 10, fontSize: 14, fontWeight: 500, fontFamily: "DM Sans, sans-serif" }}>
                            <item.icon className="w-5 h-5" />
                            {item.label}
                        </button>
                    ))}
                </nav>
                <div className="p-4 border-t" style={{ borderColor: "#F3F4F6" }}>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center overflow-hidden flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: avatarUrl ? "transparent" : PRIMARY, color: "#FFFFFF", fontWeight: 700, fontSize: 14, fontFamily: "Manrope, sans-serif" }}>
                            {avatarUrl ? <img src={avatarUrl} alt={userName} className="w-full h-full object-cover" /> : (userName || "U").charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                            <p className="truncate" style={{ color: "#111827", fontSize: 14, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>{userName}</p>
                            {email ? <p className="truncate" style={{ color: "#6B7280", fontSize: 12, fontFamily: "DM Sans, sans-serif" }}>{email}</p> : null}
                        </div>
                    </div>
                </div>
            </aside>

            <main className="flex-1 overflow-y-auto">
                {/* Desktop blue header section */}
                <div style={{ background: GRADIENT, padding: "24px 40px 28px" }}>
                    <div className="flex items-center gap-3 mb-2">
                        <button onClick={() => navigate(-1)} className="flex items-center justify-center" style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: "rgba(255,255,255,0.15)" }}>
                            <ChevronLeft className="w-5 h-5" style={{ color: "#FFFFFF" }} />
                        </button>
                        <h1 style={{ color: "#FFFFFF", fontSize: 22, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>Circle</h1>
                    </div>
                    <div style={{ maxWidth: 640, margin: "0 auto" }}>
                        {headerContent()}
                    </div>
                </div>

                {/* Desktop content */}
                <div style={{ maxWidth: 640, margin: "0 auto", padding: "30px 40px 40px" }}>
                    {membersContent()}
                </div>
            </main>
        </div>
    );
}